import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import axios from "axios";
import {toast} from "react-toastify";
import {format, isValid, parse} from "date-fns";
import projectUtils from "./project-utils.js";
import {ACTION_FEEDBACK_EVENTS, activateLatestPreparedAction, attachSwalFeedback, attachToastFeedback, clearPersistedActionFeedbackSuccess, completeActionFeedback, getActionFeedbackMessageType, isPersistenceSuccessMessage, persistActionFeedbackSuccess, resetActionFeedback, scheduleActionFeedbackNavigation} from "./action-feedback.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const API_URL = import.meta.env.VITE_API_URL;

attachSwalFeedback(Swal);
const MySwal = withReactContent(Swal);
const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

attachToastFeedback(toast);

const parseDateValue = (date) => {
    if (typeof date === "string") {
        const dateOnlyMatch = date.match(DATE_ONLY_REGEX);

        if (dateOnlyMatch) {
            const [, year, month, day] = dateOnlyMatch;
            return new Date(Number(year), Number(month) - 1, Number(day));
        }
    }

    return new Date(date);
};

// The backend sends dates in ISO while the callers declare the format they display.
// Parsing with date-fns keeps the value in local time; new Date("yyyy-MM-dd") would shift it one day back.
const ISO_DATE_FORMATS = [
    "yyyy-MM-dd'T'HH:mm:ss.SSS",
    "yyyy-MM-dd'T'HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm",
    "yyyy-MM-dd HH:mm:ss",
    "yyyy-MM-dd HH:mm",
    "yyyy-MM-dd",
];

const REQUIRED_FIELDS_MESSAGE = "Preencha os campos obrigatórios.";
const REQUIRED_FIELD_ERROR_CLASS = "!border-[#dc2626]";
const REQUIRED_FIELD_ERROR_WIDTH_CLASS = "!border-[2px]";
const REQUIRED_FIELDS_ERROR_EVENT = "required-fields-error";
export const REQUIRED_FIELDS_STATE_EVENT = "required-fields-state-change";
const REQUIRED_FIELD_SELECTOR = "[data-required-field='true'], input[required], textarea[required], select[required]";
const REQUIRED_FIELD_CANDIDATE_SELECTOR = `${REQUIRED_FIELD_SELECTOR}, input, textarea, select, button, [role='switch'], [data-required-focus='true']`;
const REQUIRED_TAB_SELECTOR = "[data-required-tab]";
const REQUIRED_ACTION_SELECTOR = "[data-required-action]";
const REQUIRED_ACTION_FOCUS_DELAY = 50;
const REQUIRED_FIELD_STYLE_PROPERTIES = ["border-color", "border-width", "box-shadow"];
const pendingRequiredFieldErrorClears = new WeakSet();
const SPECIFIC_REQUIRED_FIELD_REGEX = /^(?:o|a)\s+(?:campo|seção)\s+"([^"]+)"[\s\S]*?\s+[eé]\s+obrigat[oó]ri[oa]\b/i;
const UNQUOTED_REQUIRED_FIELD_REGEX = /^(?:o|a)\s+(.+?)\s+[eé]\s+obrigat[oó]ri[oa]\b/i;

const normalizeRequiredText = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");

const normalizeFieldName = (value) => normalizeRequiredText(value).replace(/\s*\*\s*$/, "").trim();

const getRadioGroupFields = (field) => {
    if (!field.matches("input[type='radio']") || !field.name) return [field];

    const scope = field.closest?.(REQUIRED_TAB_SELECTOR) ?? document;

    return [...scope.querySelectorAll("input[type='radio']")]
        .filter((radio) => radio.name === field.name);
};

const getSpecificRequiredFieldName = (text) => {
    const content = Array.isArray(text) ? text.join(" ") : text;
    const normalizedContent = normalizeRequiredText(content);
    const match = normalizedContent.match(SPECIFIC_REQUIRED_FIELD_REGEX) ?? normalizedContent.match(UNQUOTED_REQUIRED_FIELD_REGEX);

    return match?.[1] ?? "";
};

const isSectionRequiredError = (text) => /^a\s+seção\b/i.test(normalizeRequiredText(text));

export const triggerRequiredFieldAction = (text, fieldName = getSpecificRequiredFieldName(text)) => {
    if (typeof document === "undefined" || !fieldName) return false;

    const actionType = isSectionRequiredError(text) ? "section" : "field";
    const normalizedFieldName = normalizeFieldName(fieldName);
    const action = [...document.querySelectorAll(REQUIRED_ACTION_SELECTOR)].find((element) => {
        return element.dataset.requiredAction === actionType
            && normalizeFieldName(element.dataset.requiredActionField) === normalizedFieldName;
    });
    const button = action?.matches("button") ? action : action?.querySelector("button:not([disabled])");

    if (!button) return false;

    button.click();
    return true;
};

const isGenericRequiredError = (text) => {
    const normalizedContent = normalizeRequiredText(Array.isArray(text) ? text.join(" ") : text);

    return /^preencha (?:todos )?os campos obrigat[oó]rios\.?$/i.test(normalizedContent)
        || normalizedContent === normalizeRequiredText(REQUIRED_FIELDS_MESSAGE);
};

const isRequiredError = (text) => {
    const normalizedContent = normalizeRequiredText(Array.isArray(text) ? text.join(" ") : text);

    return isGenericRequiredError(text) || /obrigat[oó]ri[oa]/i.test(normalizedContent);
};

const isEmptyRequiredField = (field) => {
    if (field.matches("input[type='checkbox']")) {
        return field.dataset.requiredField === "true" || field.dataset.requiredChecked === "true" || field.required
            ? !field.checked
            : false;
    }

    if (field.matches("input[type='radio']")) return !getRadioGroupFields(field)
        .filter(isEnabledRequiredField)
        .some((radio) => radio.checked);

    if (field.matches("[role='switch']")) return field.getAttribute("aria-checked") !== "true" && !field.hasAttribute("data-checked");

    if (field.matches("input, textarea")) return !field.value?.trim();

    if (field.matches("select")) return !field.value;

    if (field.dataset.requiredHasValue !== undefined) return field.dataset.requiredHasValue !== "true";

    return !field.querySelector("[class*='-singleValue'], [class*='-multiValue']");
};

const setRequiredFieldError = (field, invalid) => {
    const control = field.matches("input, textarea") || field.dataset.requiredControl === "self"
        ? field
        : field.querySelector("[class*='-control']") ?? field;

    field.dataset.requiredInvalid = invalid ? "true" : "false";
    control?.classList.toggle(REQUIRED_FIELD_ERROR_CLASS, invalid);
    control?.classList.toggle(REQUIRED_FIELD_ERROR_WIDTH_CLASS, invalid);

    if (!control) return;

    if (invalid) {
        if (!field.dataset.requiredControlStyles) {
            field.dataset.requiredControlStyles = JSON.stringify(
                Object.fromEntries(REQUIRED_FIELD_STYLE_PROPERTIES.map((property) => [property, {
                    value: control.style.getPropertyValue(property),
                    priority: control.style.getPropertyPriority(property),
                }]))
            );
        }

        control.style.setProperty("border-color", "#dc2626", "important");
        control.style.setProperty("border-width", "2px", "important");

        if (!control.matches("input, textarea")) control.style.setProperty("box-shadow", "none", "important");
        return;
    }

    const previousStyles = JSON.parse(field.dataset.requiredControlStyles || "{}");

    REQUIRED_FIELD_STYLE_PROPERTIES.forEach((property) => {
        const previousStyle = previousStyles[property];

        if (previousStyle?.value) control.style.setProperty(property, previousStyle.value, previousStyle.priority);
        else control.style.removeProperty(property);
    });

    delete field.dataset.requiredControlStyles;
};

export const clearRequiredFieldError = (field) => {
    if (!field) return;

    const fields = getRadioGroupFields(field);

    if (fields.every(isEmptyRequiredField)) {
        const invalidFields = fields.filter((currentField) => currentField.dataset.requiredInvalid === "true");

        invalidFields.forEach((currentField) => setRequiredFieldError(currentField, true));
        if (invalidFields.length) dispatchRequiredFieldsState();
        return;
    }

    fields.forEach((currentField) => setRequiredFieldError(currentField, false));
    dispatchRequiredFieldsState();
};

export const resetRequiredFieldError = (field) => {
    if (!field) return;
    setRequiredFieldError(field, false);
    dispatchRequiredFieldsState();
};

const focusRequiredField = (field) => {
    const target = field.matches("input, textarea, button") || field.dataset.requiredFocus === "true" ? field : field.querySelector("input, textarea, button, [tabindex]");
    target?.focus?.();
};

const getRequiredTabForField = (field) => field.closest?.(REQUIRED_TAB_SELECTOR)?.dataset.requiredTab;

const getRequiredTabElements = (scope = document) => {
    const currentScope = scope?.current ?? scope ?? document;
    const elements = [];

    if (currentScope.matches?.(REQUIRED_TAB_SELECTOR)) elements.push(currentScope);
    elements.push(...currentScope.querySelectorAll(REQUIRED_TAB_SELECTOR));

    return elements;
};

const dispatchRequiredFieldsState = (firstInvalidTab, focusFirstInvalid = true, scope = document) => {
    if (typeof window === "undefined") return;

    window.dispatchEvent(new CustomEvent(REQUIRED_FIELDS_STATE_EVENT, {
        detail: {
            tabKeys: getRequiredTabKeys(scope),
            firstInvalidTab,
            focusFirstInvalid,
        },
    }));
};

const getFieldFromLabel = (label, scope) => {
    const wrapper = label.closest("[data-required-field='true'], [data-required-focus='true']");

    if (wrapper && (scope === document || scope.contains(wrapper))) return wrapper;

    const associatedField = label.control ?? ((label.htmlFor || label.getAttribute("for"))
        ? document.getElementById(label.htmlFor || label.getAttribute("for"))
        : null);

    if (associatedField && (scope === document || scope.contains(associatedField))) return associatedField;

    const localField = label.parentElement?.querySelector("input, textarea, select, button, [role='switch'], [data-required-field='true'], [data-required-focus='true']");

    return localField && (scope === document || scope.contains(localField)) ? localField : null;
};

const findSpecificRequiredFields = (scope, fieldName) => {
    const normalizedName = normalizeFieldName(fieldName);
    const labels = [...scope.querySelectorAll("label")];
    const fields = new Set();

    labels
        .filter((label) => normalizeFieldName(label.textContent) === normalizedName)
        .forEach((label) => {
            const field = getFieldFromLabel(label, scope);
            if (field) fields.add(field);
        });

    [...scope.querySelectorAll(REQUIRED_FIELD_CANDIDATE_SELECTOR)]
        .filter((field) => [field.id, field.getAttribute("name"), field.getAttribute("aria-label")]
            .some((value) => normalizeFieldName(value) === normalizedName))
        .forEach((field) => fields.add(field));

    return [...fields].filter(isEnabledRequiredField);
};

const notifyRequiredFieldsError = () => {
    if (typeof window === "undefined") return;

    window.dispatchEvent(new Event(REQUIRED_FIELDS_ERROR_EVENT));
};

const isVisibleRequiredField = (field) => {
    if (!field.isConnected) return false;

    if (field.closest("[hidden], [aria-hidden='true'], [inert], details:not([open])")) return false;

    for (let element = field; element && element !== document.documentElement; element = element.parentElement) {
        const {contentVisibility, display, visibility} = window.getComputedStyle(element);

        if (contentVisibility === "hidden" || display === "none" || visibility === "hidden") return false;
    }

    return true;
};

export const isEnabledRequiredField = (field) => !field.disabled && !field.closest("[aria-disabled='true'], [data-required-disabled='true'], [disabled], fieldset[disabled]");

const isValidationRequiredField = (field) => {
    if (!field.isConnected || !isEnabledRequiredField(field)) return false;

    for (let element = field; element && element !== document.documentElement; element = element.parentElement) {
        if (element.matches(REQUIRED_TAB_SELECTOR)) continue;
        if (element.matches("[hidden], [aria-hidden='true'], [inert], details:not([open])")) return false;

        const {contentVisibility, display, visibility} = window.getComputedStyle(element);

        if (contentVisibility === "hidden" || display === "none" || visibility === "hidden") return false;
    }

    return true;
};

export const getRequiredTabKeys = (scope = document) => {
    return getRequiredTabElements(scope)
        .filter((tab) => [...tab.querySelectorAll("[data-required-invalid='true']")].some(isValidationRequiredField)
            || (tab.dataset.requiredInvalid === "true" && isValidationRequiredField(tab)))
        .map((tab) => tab.dataset.requiredTab)
        .filter(Boolean);
};

export const focusRequiredTabField = (tabKey, scope = document) => {
    if (typeof document === "undefined") return;

    const tab = getRequiredTabElements(scope)
        .filter((element) => element.dataset.requiredTab === tabKey)
        .find((element) => element.matches("[data-required-invalid='true']")
            || element.querySelector("[data-required-invalid='true']"));
    const field = tab?.matches("[data-required-invalid='true']")
        ? tab
        : tab?.querySelector("[data-required-invalid='true']");

    if (field) focusRequiredField(field);
};

const getRequiredFieldsScope = () => {
    const dialogs = [...document.querySelectorAll("[role='dialog'], [aria-modal='true']")]
        .filter((dialog) => isVisibleRequiredField(dialog) && !dialog.closest(".swal2-container, .swal2-popup"));

    return dialogs.at(-1) ?? document;
};

const resolveRequiredFieldsScope = (scope) => scope?.current ?? scope ?? getRequiredFieldsScope();

const getRequiredFieldGroupKey = (field) => {
    if (field.matches("input[type='radio']") && field.name) return `radio:${field.name}`;

    return field;
};

const getUniqueRequiredFields = (fields) => {
    const seen = new Set();

    return fields.filter((field) => {
        const key = getRequiredFieldGroupKey(field);
        if (seen.has(key)) return false;

        seen.add(key);
        return true;
    });
};

export const getRequiredFieldErrorPresentation = (scope, fallbackMessage, specificFieldName = "") => {
    if (!scope) return {message: fallbackMessage, fieldName: specificFieldName, markAll: false};

    const requiredFields = [...scope.querySelectorAll(REQUIRED_FIELD_SELECTOR)]
        .filter(isValidationRequiredField);
    const invalidFields = getUniqueRequiredFields(requiredFields.filter(isEmptyRequiredField));

    if (invalidFields.length > 1) {
        return {message: REQUIRED_FIELDS_MESSAGE, fieldName: "", markAll: true};
    }

    if (isGenericRequiredError(fallbackMessage)) {
        return {message: fallbackMessage, fieldName: "", markAll: true};
    }

    return {message: fallbackMessage, fieldName: specificFieldName, markAll: false};
};

export const markRequiredFields = (fieldName = "", {focus = true, scope} = {}) => {
    if (typeof document === "undefined") return;

    const validationScope = resolveRequiredFieldsScope(scope);
    const specificFields = fieldName ? findSpecificRequiredFields(validationScope, fieldName) : [];
    const requiredFields = [...validationScope.querySelectorAll(REQUIRED_FIELD_SELECTOR)];
    requiredFields.filter((field) => !isEnabledRequiredField(field)).forEach((field) => setRequiredFieldError(field, false));
    requiredFields.filter(isEnabledRequiredField).forEach((field) => setRequiredFieldError(field, false));

    const fields = fieldName ? specificFields.filter(isValidationRequiredField) : requiredFields.filter(isValidationRequiredField);
    const invalidFields = fields.filter(isEmptyRequiredField);

    invalidFields.forEach((field) => setRequiredFieldError(field, true));

    const firstInvalidField = invalidFields[0];
    const firstInvalidTab = firstInvalidField ? getRequiredTabForField(firstInvalidField) : undefined;
    const canFocusFirstInvalid = Boolean(focus && firstInvalidField && isVisibleRequiredField(firstInvalidField));

    dispatchRequiredFieldsState(firstInvalidTab, focus && !canFocusFirstInvalid, validationScope);

    if (canFocusFirstInvalid) focusRequiredField(firstInvalidField);

    return invalidFields;
};

const handleRequiredFieldsClose = (presentation, originalMessage) => {
    const fieldName = presentation?.markAll ? "" : presentation?.fieldName;

    const actionTriggered = triggerRequiredFieldAction(originalMessage);
    markRequiredFields(fieldName, {focus: !actionTriggered});

    if (!actionTriggered) return;

    const markAfterRender = () => {
        markRequiredFields(fieldName);
    };
    const scheduleAfterRender = () => globalThis.setTimeout(markAfterRender, REQUIRED_ACTION_FOCUS_DELAY);
    const requestFrame = globalThis.requestAnimationFrame;

    if (!requestFrame) {
        scheduleAfterRender();
        return;
    }

    requestFrame(() => requestFrame(scheduleAfterRender));
};

if (typeof document !== "undefined") {
    const scheduleRequiredFieldErrorClear = (field) => {
        if (pendingRequiredFieldErrorClears.has(field)) return;

        pendingRequiredFieldErrorClears.add(field);
        globalThis.setTimeout(() => {
            pendingRequiredFieldErrorClears.delete(field);

            if (!field.isConnected) return;
            clearRequiredFieldError(field);
        }, 0);
    };

    const clearRequiredFieldFromEvent = (target) => {
        const field = target.closest?.("[data-required-field='true'], [data-required-invalid='true']") ?? target;

        if (!field.matches?.(`${REQUIRED_FIELD_SELECTOR}, [data-required-invalid='true']`)) return;

        scheduleRequiredFieldErrorClear(field);
    };

    document.addEventListener("input", ({target}) => clearRequiredFieldFromEvent(target), true);
    document.addEventListener("change", ({target}) => clearRequiredFieldFromEvent(target), true);
}

const ProjectUtils = {
    swalMessage: (type, title = '', text = '', confirmButtonText = 'Sim', showCancelButton = false, cancelButtonText = 'Não', callback = undefined, allowOutsideClick = false, icon = "warning", useHtml = false) => {

        const requiredError = !showCancelButton && isRequiredError([title, text]);
        const specificRequiredFieldName = getSpecificRequiredFieldName(text);
        const requiredFieldPresentation = requiredError
            ? getRequiredFieldErrorPresentation(getRequiredFieldsScope(), text, specificRequiredFieldName)
            : null;
        const message = requiredFieldPresentation?.message ?? text;
        const isList = Array.isArray(message);
        const content = isList ? message.join('<br>') : message;
        const renderHtml = useHtml || isList;

        if (!showCancelButton && (type === "error" || type === "warning" || icon === "error" || icon === "warning")) {
            resetActionFeedback();
        }

        if (requiredError) notifyRequiredFieldsError();

        MySwal.fire({
            icon,
            type,
            title,
            ...(renderHtml ? { html: content } : { text: content }),
            confirmButtonColor: '#063A6C',
            confirmButtonText,
            showCancelButton,
            cancelButtonText,
            cancelButtonColor: '#c10007',
            allowOutsideClick,
            reverseButtons: true,
            returnFocus: !requiredError,
            didClose: requiredError
                ? () => handleRequiredFieldsClose(requiredFieldPresentation, text)
                : undefined,
        }).then((result) => {
            if (showCancelButton && result?.isConfirmed) activateLatestPreparedAction();
            else if (!result?.isConfirmed) resetActionFeedback(undefined, ACTION_FEEDBACK_EVENTS.cancel);
            callback?.(result);
        });
    },

    swalSuccessMessage: (type, title = '', text = '', confirmButtonText = 'Sim', showCancelButton = false, cancelButtonText = 'Não', callback = undefined, allowOutsideClick = false, useHtml = false, cancelButtonColor = '#c10007', customClass = {}) => {
        const handled = isPersistenceSuccessMessage(text)
            && completeActionFeedback(getActionFeedbackMessageType(text), text);

        if (handled && !showCancelButton) {
            scheduleActionFeedbackNavigation();
            callback?.();
            return;
        }

        MySwal.fire({
            icon: "success",
            title,
            text,
            confirmButtonColor: '#063A6C',
            confirmButtonText,
            showCancelButton,
            cancelButtonText,
            cancelButtonColor,
            customClass,
            allowOutsideClick,
            reverseButtons: true,
            ...(useHtml ? { html: text } : { text }),
        }).then(callback);
    },

    swalErrorMessage: (title = '', text = '', confirmButtonText = 'Ok', showCancelButton = false, cancelButtonText = 'Não', callback = undefined, useHtml = false, customClass = {}) => {

        const requiredError = isRequiredError([title, text]);
        const specificRequiredFieldName = getSpecificRequiredFieldName(text);
        const requiredFieldPresentation = requiredError
            ? getRequiredFieldErrorPresentation(getRequiredFieldsScope(), text, specificRequiredFieldName)
            : null;
        const message = requiredError ? REQUIRED_FIELDS_MESSAGE : text;
        const isList = Array.isArray(message);
        const content = isList ? message.join('<br>') : message;
        const renderHtml = useHtml || isList;

        resetActionFeedback();

        if (requiredError) notifyRequiredFieldsError();

        MySwal.fire({
            icon: "error",
            title,
            showCancelButton,
            ...(renderHtml ? { html: content } : { text: content }),
            confirmButtonText: requiredError ? "OK" : confirmButtonText,
            cancelButtonText,
            confirmButtonColor: '#063A6C',
            reverseButtons: true,
            returnFocus: !requiredError,
            didClose: requiredError
                ? () => handleRequiredFieldsClose(requiredFieldPresentation, text)
                : undefined,
            customClass
        }).then((result) => {
            callback?.(result);
        });
    },

    addressToString: (city, neighborhood, street, number) => {
        const addressValue = [
            neighborhood,
            street && (neighborhood ? " - " : "") + street,
            number && (neighborhood || street ? ", " : "") + number,
        ].filter(Boolean).join('');

        return city && addressValue ? city + ', ' + addressValue : city ?? "";
    },

    formatPhone: (phone) => {
        if (phone) {
            phone = phone.toString();

            if (phone.length === 11) return phone.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
            if (phone.length === 10) return phone.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
        }

        return phone;
    },

    formatDate: (date, format = 'DD/MM/YYYY hh:mm', cutSeconds = false) => {
        if (!date) return null;

        if (typeof date === "string") {
            const formattedDateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
            const formattedDateTimeRegex = /^\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}(:\d{2})?$/;

            if (formattedDateRegex.test(date) || formattedDateTimeRegex.test(date)) {
                if (cutSeconds) return date.replace(/:\d{2}$/, '');
                return date;
            }
        }

        const dateObj = parseDateValue(date);

        if (format === 'DD/MM/YYYY') return dateObj.toLocaleDateString('pt-BR');
        if (format === 'DD/MM/YYYY hh:mm:ss') return (dateObj.toLocaleDateString('pt-BR') + ' ' + dateObj.toLocaleTimeString('pt-BR'));

        return (dateObj.toLocaleDateString('pt-BR') + ' ' + dateObj.toLocaleTimeString('pt-BR', { timeStyle: 'short' }));
    },

    toDate: (value, format = "dd/MM/yyyy HH:mm") => {
        if (!value) return null;

        if (value instanceof Date) return isValid(value) ? value : null;

        for (const pattern of [format, ...ISO_DATE_FORMATS]) {
            const parsed = parse(value, pattern, new Date());
            if (isValid(parsed)) return parsed;
        }

        return null;
    },

    formatPlate: (valor) => {
        if (!valor) return null;

        const clearPlate = valor.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const regex = /^([A-Z0-9]{3})([A-Z0-9]{1,4})$/;

        if (regex.test(clearPlate)) return clearPlate.replace(regex, '$1-$2');

        return clearPlate;
    },

    formatUniversalPlate: (value) => {
        if (!value) return null;
        const plate = String(value).toUpperCase().replace(/[^A-Z0-9]/g, "");

        // Brasil Cinza - 'AAA-0000'
        if (/^[A-Z]{3}\d{4}$/.test(plate)) return `${plate.slice(0, 3)}-${plate.slice(3)}`;

        // Brasil Mercosul - 'AAA 0A00'
        if (/^[A-Z]{3}\d[A-Z]\d{2}$/.test(plate)) return `${plate.slice(0, 3)} ${plate.slice(3)}`;

        // Ciclomotor - 'AAA00AA'
        if (/^[A-Z]{3}\d{2}[A-Z]{2}$/.test(plate)) return plate;

        // Bolivia - '0000AAA'
        if (/^\d{4}[A-Z]{3}$/.test(plate)) return plate;

        // Argentina - 'AA 000 AA'
        if (/^[A-Z]{2}\d{3}[A-Z]{2}$/.test(plate)) return `${plate.slice(0, 2)} ${plate.slice(2, 5)} ${plate.slice(5)}`;

        // Paraguai Carro - 'AAAA 000'
        if (/^[A-Z]{4}\d{3}$/.test(plate)) return `${plate.slice(0, 4)} ${plate.slice(4)}`;

        // Paraguai Moto - '000 AAAA'
        if (/^\d{3}[A-Z]{4}$/.test(plate)) return `${plate.slice(0, 3)} ${plate.slice(3)}`;

        return plate;
    },

    formatCpfToView: (cpf) => {
        let cpfFormated = ''

        if (cpf) cpfFormated = cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
        return cpfFormated;
    },

    formatCnpjToView: (cnpj) => {
        let cnpjFormated = '';

        if (cnpj) cnpjFormated = cnpj.replace(/(\w{2})(\w{3})(\w{3})(\w{4})(\w{2})/, "$1.$2.$3/$4-$5");
        return cnpjFormated;
    },

    formatCpfCnpjToView: (value) => {
        if (!value) return value;
        const digits = value.toString().replace(/\D/g, "");

        if (digits.length === 11) return projectUtils.formatCpfToView(digits);
        if (digits.length === 14) return projectUtils.formatCnpjToView(digits);

        return value;
    },

    formatDateInput: (value, hasTime, mode = "start") => {
        if (!value) return null;

        const digits = value.replace(/\D/g, '');
        const expectedLength = hasTime ? 12 : 8;
        const padded = digits.padEnd(expectedLength, '0');

        const d = padded.slice(0, 2);
        const m = padded.slice(2, 4);
        const y = padded.slice(4, 8);

        let h = '00';
        let min = '00';

        if (hasTime) {
            const inputHour = digits.slice(8, 10);
            const inputMin = digits.slice(10, 12);

            if (inputHour || inputMin) {
                h = padded.slice(8, 10);
                min = padded.slice(10, 12);
            } else if (mode === "end") {
                h = '23';
                min = '59';
            }

        }

        const formatted = hasTime ? `${d}/${m}/${y} ${h}:${min}` : `${d}/${m}/${y}`;
        let date = null;

        if (Number(d) >= 1 && Number(m) >= 1 && Number(y) >= 1) {
            const safeD = d.padStart(2, '0');
            const safeM = m.padStart(2, '0');
            const safeY = y.padStart(4, '0');

            const iso = `${safeY}-${safeM}-${safeD}T${h}:${min}:00`;

            const parsed = new Date(iso);
            if (!isNaN(parsed.getTime())) date = parsed;
        }

        return { formatted, date };
    },

    formatTimeInput: (value) => {
        if (!value) return null;
        const digits = value.replace(/\D/g, '');

        const h = digits.slice(0,2) ?? "00";
        const m = digits.slice(2,4) ?? "00";
        const s = digits.slice(4,6) ?? "00";

        const dateRef = new Date(new Date().setHours(Number(h), Number(m), Number(s)))

        return { formatted: format(dateRef, 'dd/MM/yyyy HH:mm:ss'), date: dateRef };
    },

    formatHourInput: (value, hasSeconds = true) => {
        if (!value) return null;
        const digits = value.replace(/\D/g, '');

        const h = digits.slice(0,2) ?? "00";
        const m = digits.slice(2,4) ?? "00";
        const s = digits.slice(4,6) ?? "00";

        const timeFormat = hasSeconds ? 'HH:mm:ss' : 'HH:mm';
        const dateRef = new Date(new Date().setHours(Number(h), Number(m), Number(s)))
        return { formatted: format(dateRef, timeFormat), date: dateRef };
    },

    hasValue: (value) => {
        return value !== null && value !== "" && value !== undefined
    },

    removeAccents: (str) => {
        if (!str) return '';

        const accents = 'ÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž';
        const accentsOut = 'AAAAAAaaaaaaOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz';

        str = str.split('');

        const strLen = str.length;

        let x;
        for (let i = 0; i < strLen; i++) {
            if ((x = accents.indexOf(str[i])) != -1) {
                str[i] = accentsOut[x];
            }
        }

        return str.join('');
    },

    openDvr: (id) => {
        axios.post(`${API_KEEPER}/contactid/place/${id}/opendvr`)
            .then(() => {
                ProjectUtils.swalSuccessMessage("success", "", "O DVR será aberto", "Ok");
            })
            .catch(error => {
                console.error(error);
                ProjectUtils.swalErrorMessage("", "Ocorreu um erro ao abrir o DVR", "Ok");
            });
    },

    copyToClipboard: async (textToCopy, message) => {
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';

        document.body.appendChild(textarea);
        textarea.select();

        try {
            document.execCommand('copy');
        } catch (err) {
            console.error(err);
        } finally {
            toast.success(message);
            document.body.removeChild(textarea);
        }
    },

    normalizeData: (dataObject) => {
        if (dataObject === null) return "";
        if (dataObject === undefined) return "";

        if (Array.isArray(dataObject)) return dataObject.map((item) => ProjectUtils.normalizeData(item));

        if (typeof dataObject === "object") {
            const newObj = {};
            for (const key in dataObject) {
                newObj[key] = ProjectUtils.normalizeData(dataObject[key]);
            }
            return newObj;
        }

        return dataObject;
    },

    getFileFromUrl: async (url, name, type) => {
        type = type === 'pdf' ? 'application/pdf' : type;
        const response = await fetch(url);
        const data = await response.blob();

        return new File([data], name, {type});
    },

    openOccurrence: (occId) => {
        axios.get(`${API_URL}/occurrence/openSentryView/${occId}`)
            .then(() => {
                toast.success("A ocorrência associada será aberta no SentryView");
            })
            .catch(error => {
                console.error(error);
                ProjectUtils.swalErrorMessage("", "Ocorreu um erro ao abrir a ocorrência no SentryView.", "Ok");
            });
    },

    createOccurrence: (occId, endpoint) => {
        axios.post(API_URL + endpoint, { id: occId })
            .then(() => {
                toast.success("O cadastro de ocorrência será aberto no SentryView.");
            })
            .catch(error => {
                console.error(error);
                ProjectUtils.swalErrorMessage("", "Ocorreu um erro ao abrir o cadastro no SentryView.", "Ok");
            });
    },

    exportCsvData: (csvMaker, data) => {
        const csvDatas = [];

        const headers = Object.keys(data[0]);
        csvDatas.push(headers.join(';'));

        data.map((item) => csvDatas.push(csvMaker(item)));

        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvDatas.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);

        window.open(url);
        window.URL.revokeObjectURL(url);
        URL.revokeObjectURL(url);
    },

    prepareUrl: (path) => {
        const first = path.indexOf('/');
        const second = path.indexOf('/', first + 1);

        if (second === -1) return path;
        return API_URL + path.substring(second);
    },

    success: (reload = false, message = "Salvo com sucesso") => {
        const type = getActionFeedbackMessageType(message);

        if (reload) {
            persistActionFeedbackSuccess(type, message);
            return false;
        }

        if (completeActionFeedback(type, message)) {
            scheduleActionFeedbackNavigation();
            return true;
        }

        return false;
    },

    error: (reload = false, message = "Não foi possível salvar.") => {
        clearPersistedActionFeedbackSuccess();
        resetActionFeedback();
        if (reload) localStorage.setItem("toast-error", message);
        else toast.error(message);
    },

    formatTimeString(date){
        if (!date) return null
        return date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    },

    safeJsonParse(value, fallback = {}) {
        try {
            return typeof value === "string" ? JSON.parse(value) : value ?? fallback;
        } catch {
            return fallback;
        }
    },

    createTempKey() {
        return crypto?.randomUUID?.() ?? Date.now();
    },

    replaceEmptyStringsWithNull(val) {
        if (val === "") return null;
        if (typeof val !== "object" || val === null) return val;

        if (Array.isArray(val)) return val.map(projectUtils.replaceEmptyStringsWithNull);

        const cleanedObj = {};
        for (const key in val) {
            cleanedObj[key] = projectUtils.replaceEmptyStringsWithNull(val[key]);
        }

        return cleanedObj;
    },

    translateImgPath(path) {
        if (!path) return "";

        if (import.meta.env.DEV) {

            const proxies = ["keeper", "archiver", "despacho"];

            const pattern = new RegExp(`\\/(${proxies.join("|")})`, "i");

            return path.replace(pattern, "/$1Proxy");
        }

        return path;
    },

    googleMapsIds(){
        return {
            "default": "a7e2dbd01b9a18afb82d00c6",
            "poi-street": "a7e2dbd01b9a18af88733809",
            "poi-nostreet": "a7e2dbd01b9a18af474af8f8",
            "nopoi-nostreet": "a7e2dbd01b9a18af197654b3"
        }
    },

    changeEmptyValuesToNull(obj) {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            acc[key] = value === '' ? null : value;
            return acc;
        }, {});
    },

    removeAttributesWithNoValue(obj) {
        if (typeof obj !== 'object' || obj === null) return obj;

        if (Array.isArray(obj)) {
            return obj
              .map(item => this.removeAttributesWithNoValue(item))
              .filter(item => item !== undefined && item !== null && item !== '');
        }

        return Object.fromEntries(
          Object.entries(obj)
            .map(([key, value]) => [key, this.removeAttributesWithNoValue(value)]) // Chamada recursiva
            .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        );
    },

    formatFileSize(bytes) {
        if (!bytes) return "0 B";
        if (bytes < 1024) return `${bytes} B`;

        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(2).replace(".", ",")} KB`;

        const mb = kb / 1024;
        if (mb < 1024) return `${mb.toFixed(2).replace(".", ",")} MB`;
    },

    formatMillisecondsToDuration(value) {
        if (value === null || value === undefined) return "";

        const totalSeconds = Math.floor(value / 1000);

        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${String(minutes).padStart(2, "0")} min. ${String(seconds).padStart(2, "0")} seg.`;
    },
    formatToSelect(data, fromValue = "id", fromLabel = "text"){
        if(!data) return [];
        return data.map(el => ({value: el[fromValue], label: el[fromLabel]}))
    },

    sentenceCase(value) {
        if (typeof value !== "string" || !value.trim()) return "";

        const text = value.trim().toLocaleLowerCase("pt-BR");

        return text.charAt(0).toLocaleUpperCase("pt-BR") + text.slice(1);
    },
}

export default ProjectUtils;